import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { FaEllipsisV, FaTimes } from "react-icons/fa";

export interface TableColumn<T = any> {
  key: keyof T;
  label: string;
  getClassName?: (value: any, row?: T) => string;
  responsive?: "all" | "sm" | "md" | "lg";
}

export interface MenuOption<T = any> {
  label: string;
  color?: string;
  icon?: React.ReactNode; // ✅ add this
  onClick: (row: T) => void;
}


interface TableProps<T> {
  columns: TableColumn<T>[];
  data: T[];
  rowsPerPage?: number;
  tableMenu?: (row: T, closeMenu: () => void) => React.ReactNode;
}

export const Table = <T extends Record<string, any>>({
  columns,
  data,
  rowsPerPage = 10,
  tableMenu,
}: TableProps<T>) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil(data.length / rowsPerPage);

  const startIndex = (page - 1) * rowsPerPage;
  const rowsToShow = data.slice(startIndex, startIndex + rowsPerPage);

  const [openMenuIndex, setOpenMenuIndex] = useState<number | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const closeMenu = () => setOpenMenuIndex(null);

  const toggleMenu = (index: number) => {
    if (openMenuIndex === index) {
      closeMenu();
    } else {
      const button = buttonRefs.current[index];
      if (button) {
        const rect = button.getBoundingClientRect();
        const menuHeight = 150;
        const menuWidth = 140;
        const spaceBelow = window.innerHeight - rect.bottom;

        const top = spaceBelow >= menuHeight
          ? rect.bottom + window.scrollY
          : rect.top + window.scrollY - menuHeight;

        const left = Math.min(rect.right - menuWidth, window.innerWidth - menuWidth);
        setMenuPosition({ top, left });
      }
      setOpenMenuIndex(index);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        openMenuIndex !== null &&
        buttonRefs.current[openMenuIndex] &&
        !buttonRefs.current[openMenuIndex]!.contains(event.target as Node)
      ) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openMenuIndex]);

  return (
    <div className="w-full max-w-full border border-gray-200 rounded-lg bg-white overflow-hidden">
      <table className="w-full table-auto border-collapse">
        <thead>
          <tr className="bg-gray-100 text-left">
            {columns.map((col) => {
              const visibility =
                col.responsive === "sm"
                  ? "hidden sm:table-cell"
                  : col.responsive === "md"
                  ? "hidden md:table-cell"
                  : col.responsive === "lg"
                  ? "hidden lg:table-cell"
                  : "table-cell";

              return (
                <th
                  key={String(col.key)}
                  className={`px-4 py-2 font-semibold text-gray-700 border-b ${visibility}`}
                >
                  {col.label}
                </th>
              );
            })}
            {tableMenu && <th className="px-4 py-2 border-b text-right"></th>}
          </tr>
        </thead>

        <tbody>
          {rowsToShow.map((row, idx) => (
            <tr key={idx} className="border-b last:border-b-0 hover:bg-gray-50">
              {columns.map((col) => {
                const value = row[col.key];
                const customClass = col.getClassName ? col.getClassName(value, row) : "text-gray-700";
                const visibility =
                  col.responsive === "sm"
                    ? "hidden sm:table-cell"
                    : col.responsive === "md"
                    ? "hidden md:table-cell"
                    : col.responsive === "lg"
                    ? "hidden lg:table-cell"
                    : "table-cell";

                return (
                  <td key={String(col.key)} className={`px-4 py-2 ${customClass} ${visibility}`}>
                    {value}
                  </td>
                );
              })}

              {tableMenu && (
                <td className="px-4 py-2 text-right">
                  <button
                    ref={(el) => (buttonRefs.current[idx] = el)}
                    onClick={() => toggleMenu(idx)}
                    className="text-gray-600 w-8 hover:text-gray-900 focus:outline-none"
                  >
                    {openMenuIndex === idx ? FaTimes({}) : FaEllipsisV({})}
                  </button>
                </td>
              )}
            </tr>
          ))}

          {rowsToShow.length === 0 && (
            <tr>
              <td
                colSpan={columns.length + (tableMenu ? 1 : 0)}
                className="px-4 py-2 text-center text-gray-500"
              >
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ✅ Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-t text-sm text-gray-600">
          <span>
            Page {page} of {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={`px-3 py-1 rounded-md ${
                page === 1
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              Prev
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className={`px-3 py-1 rounded-md ${
                page === totalPages
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
              }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {openMenuIndex !== null && tableMenu &&
        createPortal(
          <div
            style={{
              position: "absolute",
              top: menuPosition.top,
              left: menuPosition.left,
              zIndex: 9999,
              width: 150,
            }}
            className="bg-white border border-gray-200 rounded shadow-lg"
          >
            {tableMenu(rowsToShow[openMenuIndex], closeMenu)}
          </div>,
          document.body
        )}
    </div>
  );
};
export const TableMenu: React.FC<{
  row: any;
  options: MenuOption<any>[];
  closeMenu: () => void;
}> = ({ row, options, closeMenu }) => (
  <div className="relative overflow-visible">
  <div
    className="absolute right-0 top-full mt-2 flex flex-col w-44 bg-white rounded-xl overflow-hidden py-1 z-50"
    style={{
      border: "1px solid rgba(229, 231, 235, 1)",
      borderTop: "1px solid rgba(255,255,255,0.3)",
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
    }}
    onMouseDown={(e) => e.stopPropagation()}
  >
    {options.map((opt, index) => (
      <button
        key={index}
        className={`flex items-center gap-2 w-full px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 active:bg-gray-200 transition ${
          opt.color || ""
        }`}
        onClick={() => {
          opt.onClick(row);
          closeMenu();
        }}
      >
        {opt.icon && <span className="text-gray-500">{opt.icon}</span>}
        <span>{opt.label}</span>
      </button>
    ))}
  </div>
</div>

);
